/**
 * §P.11 · Los pedidos agrupados, y la lista para el proveedor.
 *
 * ## `/summary` es lo más útil de toda la función, y por eso abre aquí
 *
 * Es la lista que hoy el maestro hace a mano y con errores cada septiembre,
 * consolidada por artículo y talla. **Al proveedor no se le pide «una 130 para Lucía
 * y una 130 para Marco»: se le piden dos doboks del 130.** La pantalla enseña las dos
 * caras —la línea consolidada y quién hay detrás— porque cuando la caja llega hay que
 * saber de quién es cada dobok, que es exactamente el problema del septiembre
 * siguiente.
 *
 * ## Los cuatro estados no son decoración
 *
 * `open` recoge solicitudes, `closed` deja de recogerlas, `ordered` ya está mandado y
 * `received` ha llegado. **Cada uno cambia lo que la familia ve**, así que se cambian
 * de uno en uno y con el nombre que entiende quien lo pulsa.
 *
 * ## La fecha límite es lo que hace que un pedido se cierre
 *
 * Un pedido «abierto» sin fecha se queda abierto hasta diciembre y el proveedor
 * entrega en enero. Por eso se pide al abrirlo y se puede mover, pero no se esconde.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { cuenta, euros, fecha, fechaParaInput } from '@/shared/ui/formato';
import {
  ESTADOS_DE_CAMPANA,
  etiquetaDeCategoria,
  type CampanaDeMaterial,
  type EstadoDeCampana,
  type ResumenDelPedido,
} from '@/club/material/tipos';

/** El paso siguiente de un pedido, que es el único que se ofrece. */
function siguiente(
  estado: EstadoDeCampana
): { id: EstadoDeCampana; etiqueta: string; explica: string } | null {
  if (estado === 'open')
    return {
      id: 'closed',
      etiqueta: 'Cerrar el plazo',
      explica: 'Las familias dejan de poder pedir.',
    };
  if (estado === 'closed')
    return {
      id: 'ordered',
      etiqueta: 'Ya se lo he pedido al proveedor',
      explica: 'La familia verá que su pedido está en camino.',
    };
  if (estado === 'ordered')
    return {
      id: 'received',
      etiqueta: 'Ha llegado',
      explica: 'Avisa de que se puede recoger en el club.',
    };
  return null;
}

export function Pedidos({
  club,
  puedeEditar,
}: {
  club: string;
  puedeEditar: boolean;
}) {
  const cache = useQueryClient();
  const [abierto, setAbierto] = useState<string | null>(null);
  const [creando, setCreando] = useState(false);
  const [nombre, setNombre] = useState('');
  const [cierre, setCierre] = useState('');
  const [problema, setProblema] = useState<Problema | null>(null);

  const campanas = useQuery({
    queryKey: ['campanas', club],
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        ['material-campaigns'].get({ query: {} });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as CampanaDeMaterial[];
    },
  });

  const resumen = useQuery({
    queryKey: ['resumen-pedido', club, abierto],
    enabled: abierto !== null,
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        ['material-campaigns']({ id: abierto! })
        .summary.get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as ResumenDelPedido;
    },
  });

  const refrescar = () => {
    setProblema(null);
    void cache.invalidateQueries({ queryKey: ['campanas', club] });
    void cache.invalidateQueries({ queryKey: ['resumen-pedido', club] });
  };

  const abrir = useMutation({
    mutationFn: async () => {
      const r = await api.v1.clubs({ club })['material-campaigns'].post({
        name: nombre.trim(),
        ...(cierre === '' ? {} : { closes_on: cierre }),
      });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setCreando(false);
      setNombre('');
      setCierre('');
      refrescar();
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  const cambiar = useMutation({
    mutationFn: async ({
      id,
      cambios,
    }: {
      id: string;
      cambios: Record<string, unknown>;
    }) => {
      const r = await api.v1
        .clubs({ club })
        ['material-campaigns']({ id })
        .patch(cambios);
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: refrescar,
    onError: (e) => setProblema(aProblema(e)),
  });

  if (campanas.isPending) {
    return <Cargando filas={3} etiqueta="Cargando los pedidos" />;
  }

  if (campanas.isError || !campanas.data) {
    return (
      <PanelError
        problema={aProblema(campanas.error)}
        reintentar={() => void campanas.refetch()}
      />
    );
  }

  return (
    <section>
      <header className="titulo-fila">
        <h1>Pedidos agrupados</h1>
        {puedeEditar && !creando && (
          <button
            type="button"
            className="btn-primario"
            onClick={() => setCreando(true)}
          >
            Abrir un pedido
          </button>
        )}
      </header>

      <p className="tenue">
        Recoges lo que pide cada familia y te sale{' '}
        <strong>una sola lista con las tallas sumadas</strong> para pasarle al
        proveedor. Es lo que hoy se hace a mano cada septiembre.
      </p>

      {problema && <PanelError problema={problema} />}

      {creando && (
        <form
          className="formulario"
          onSubmit={(e) => {
            e.preventDefault();
            setProblema(null);
            abrir.mutate();
          }}
        >
          <div className="rejilla">
            <label>
              Cómo se llama
              <input
                required
                maxLength={160}
                placeholder="Pedido de septiembre"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
              />
            </label>

            <label>
              Hasta cuándo se puede pedir
              <input
                type="date"
                value={cierre}
                onChange={(e) => setCierre(e.target.value)}
              />
            </label>
          </div>

          {/*
            La fecha no es obligatoria en el contrato y aquí se pide igual: un pedido
            abierto sin fecha se queda abierto hasta diciembre y el proveedor entrega
            en enero. Se avisa en vez de imponerla.
          */}
          {cierre === '' && (
            <p className="nota">
              <strong>Sin fecha límite, el pedido no se cierra solo.</strong>{' '}
              Las familias siguen pidiendo en noviembre y el proveedor entrega
              tarde.
            </p>
          )}

          <div className="acciones">
            <button
              type="submit"
              className="btn-primario"
              disabled={abrir.isPending}
            >
              {abrir.isPending ? 'Abriendo…' : 'Abrir el pedido'}
            </button>
            <button
              type="button"
              className="btn-plano"
              onClick={() => setCreando(false)}
            >
              Dejarlo
            </button>
          </div>
        </form>
      )}

      {campanas.data.length === 0 ? (
        <Vacio
          titulo="No hay ningún pedido abierto"
          explicacion="Un pedido agrupado recoge lo que quiere cada familia y lo consolida por talla. Se abre en septiembre y se cierra cuando toca encargar."
        />
      ) : (
        <ul className="papeles">
          {campanas.data.map((c) => {
            const paso = siguiente(c.status);

            return (
              <li key={c.id}>
                <div className="aviso-cabeza">
                  <strong>{c.name}</strong>
                  <span
                    className={`pastilla pastilla-left ${
                      c.status === 'open' ? 'pastilla-bien' : ''
                    }`}
                  >
                    {ESTADOS_DE_CAMPANA[c.status]}
                  </span>
                </div>

                <p className="tenue">
                  {cuenta(c.request_count, 'solicitud', 'solicitudes')} ·{' '}
                  {euros(c.total_cents)}
                  {c.closes_on
                    ? ` · se cierra el ${fecha(c.closes_on)}`
                    : ' · sin fecha de cierre'}
                </p>

                <div className="acciones">
                  <button
                    type="button"
                    className="btn-plano"
                    onClick={() => setAbierto(abierto === c.id ? null : c.id)}
                  >
                    {abierto === c.id
                      ? 'Cerrar la lista'
                      : 'Ver la lista para el proveedor'}
                  </button>

                  {puedeEditar && paso && (
                    <button
                      type="button"
                      className="btn-plano"
                      disabled={cambiar.isPending}
                      title={paso.explica}
                      onClick={() =>
                        cambiar.mutate({
                          id: c.id,
                          cambios: { status: paso.id },
                        })
                      }
                    >
                      {paso.etiqueta}
                    </button>
                  )}

                  {puedeEditar && c.status === 'open' && (
                    <label className="fecha-en-linea">
                      Mover la fecha
                      <input
                        type="date"
                        defaultValue={fechaParaInput(c.closes_on)}
                        onChange={(e) => {
                          if (e.target.value === '') return;
                          cambiar.mutate({
                            id: c.id,
                            cambios: { closes_on: e.target.value },
                          });
                        }}
                      />
                    </label>
                  )}
                </div>

                {/* ── La lista consolidada ─────────────────────────────── */}
                {abierto === c.id && (
                  <>
                    {resumen.isPending ? (
                      <Cargando filas={3} etiqueta="Cargando la lista" />
                    ) : resumen.isError || !resumen.data ? (
                      <PanelError problema={aProblema(resumen.error)} />
                    ) : resumen.data.lines.length === 0 ? (
                      <p className="tenue">
                        Todavía no ha pedido nadie. La lista aparece en cuanto
                        llegue la primera solicitud.
                      </p>
                    ) : (
                      <>
                        <div className="tabla-envoltura">
                          <table className="tabla">
                            <thead>
                              <tr>
                                <th>Artículo</th>
                                <th>Talla</th>
                                <th className="num">Cuántos</th>
                                <th className="num">Importe</th>
                                <th>Para quién</th>
                              </tr>
                            </thead>
                            <tbody>
                              {resumen.data.lines.map((l, i) => (
                                <tr
                                  key={`${l.catalog_item_id}-${l.size ?? ''}-${i}`}
                                >
                                  <td>
                                    {l.item_name}
                                    <span className="tenue">
                                      {' '}
                                      · {etiquetaDeCategoria(l.category)}
                                    </span>
                                  </td>
                                  <td>{l.size ?? 'Sin talla'}</td>
                                  <td className="num">{l.quantity}</td>
                                  <td className="num">
                                    {euros(l.total_cents)}
                                  </td>
                                  {/*
                                    Quién hay detrás de cada línea. Sin esto la caja
                                    llega y nadie sabe de quién es cada dobok, que es
                                    el problema del septiembre siguiente.
                                  */}
                                  <td className="tenue">
                                    {l.students.join(', ')}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        <p className="tenue">
                          <strong>
                            {cuenta(
                              resumen.data.total_items,
                              'artículo',
                              'artículos'
                            )}{' '}
                            · {euros(resumen.data.total_cents)}
                          </strong>{' '}
                          en total. Esta es la lista que se le pasa al
                          proveedor: por artículo y talla, no por alumno.
                        </p>
                      </>
                    )}
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
